(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else root.IllegalCartCoordinator = api;
})(typeof window === "undefined" ? globalThis : window, function () {
  "use strict";

  const canonical = (value) => {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === "object")
      return Object.fromEntries(
        Object.keys(value)
          .sort()
          .map((key) => [key, canonical(value[key])]),
      );
    return value;
  };
  const properties = (value) =>
    Object.fromEntries(
      Object.entries(
        Array.isArray(value) ? Object.fromEntries(value) : value || {},
      )
        .filter(([, v]) => v !== "" && v != null)
        .map(([k, v]) => [k, String(v)]),
    );
  const identity = (item) =>
    JSON.stringify(
      canonical({
        variant: String(item.variant_id || item.id),
        properties: properties(item.properties),
        plan: String(
          item.selling_plan_allocation?.selling_plan?.id ||
            item.selling_plan ||
            "",
        ),
        parent: item.parent_relationship || null,
        components: item.item_components || null,
      }),
    );
  const basket = (cart) =>
    JSON.stringify(
      (cart.items || [])
        .map((item) => [identity(item), item.quantity])
        .sort((a, b) => a[0].localeCompare(b[0])),
    );
  const failure = (code, message) =>
    Object.assign(new Error(message || code), { code });
  function validate(cart) {
    if (
      !cart ||
      !Array.isArray(cart.items) ||
      !Number.isSafeInteger(cart.item_count) ||
      !Number.isFinite(cart.total_price) ||
      !Number.isFinite(cart.original_total_price) ||
      cart.items.some(
        (i) =>
          !i.key ||
          !(i.variant_id || i.id) ||
          !Number.isSafeInteger(i.quantity) ||
          i.quantity < 1 ||
          !Number.isFinite(i.final_line_price),
      ) ||
      cart.items.reduce((sum, i) => sum + i.quantity, 0) !== cart.item_count
    )
      throw failure("malformed");
    return cart;
  }

  class Coordinator {
    constructor({
      fetch: transport,
      routes = {},
      cart,
      timeout = 12000,
      onChange = () => {},
    }) {
      this.fetch = transport;
      this.routes = routes;
      this.timeout = timeout;
      this.cart = validate(cart);
      this.onChange = onChange;
      this.tail = Promise.resolve();
      this.pending = 0;
      this.intents = new Map();
      this.aliases = new Map(this.cart.items.map((i) => [i.key, identity(i)]));
      this.adds = new Map();
      this.locked = false;
      this.error = null;
      this.uncertain = null;
      this.revision = 0;
      this.listeners = new Set();
    }
    state() {
      return {
        cart: this.cart,
        pending: this.pending + this.intents.size,
        locked: this.locked,
        error: this.error,
        revision: this.revision,
      };
    }
    emit() {
      try {
        this.onChange(this.state());
      } catch (_) {}
      this.listeners.forEach((listener) => {
        try { listener(this.state()); } catch (_) {}
      });
    }
    subscribe(listener) {
      this.listeners.add(listener);
      return () => this.listeners.delete(listener);
    }
    route(type) {
      return (
        (this.routes["cart_" + type + "_url"] ||
          (this.routes.cart_url || "/cart") + "/" + type) + ".js"
      );
    }
    accept(cart) {
      validate(cart);
      this.cart = cart;
      cart.items.forEach((i) => this.aliases.set(i.key, identity(i)));
      this.revision++;
      this.emit();
      return cart;
    }
    resolve(signature, cart = this.cart) {
      const matches = cart.items.filter((i) => identity(i) === signature);
      if (matches.length > 1) throw failure("ambiguous");
      return matches[0];
    }
    desired(key) {
      const signature = this.aliases.get(key);
      return (
        this.intents.get(signature)?.quantity ??
        this.resolve(signature)?.quantity ??
        0
      );
    }
    enqueue(task, allowError = false) {
      this.pending++;
      this.emit();
      const run = this.tail
        .then(async () => {
          if (this.error && !allowError) throw this.error;
          return task();
        })
        .catch((error) => {
          this.error = error;
          this.emit();
          throw error;
        })
        .finally(() => {
          this.pending--;
          this.emit();
        });
      this.tail = run.catch(() => {});
      return run;
    }
    async request(url, options = {}, expectation) {
      let timer;
      let settled = false;
      const write = options.method === "POST";
      const record = { expectation, settled: false };
      const request = Promise.resolve()
        .then(() =>
          this.fetch(url, {
            credentials: "same-origin",
            cache: "no-store",
            ...options,
          }),
        )
        .then(async (response) => {
          if (!response.ok) {
            let message;
            try {
              const data = await response.json();
              message =
                typeof data.description === "string"
                  ? data.description
                  : undefined;
            } catch (_) {}
            throw failure(
              response.status >= 500 ? "uncertain" : "rejected",
              message,
            );
          }
          try {
            return await response.json();
          } catch (_) {
            throw failure("malformed");
          }
        })
        .finally(() => {
          settled = true;
          record.settled = true;
        });
      try {
        return await Promise.race([
          request,
          new Promise((_, reject) => {
            timer = setTimeout(() => reject(failure("timeout")), this.timeout);
          }),
        ]);
      } catch (error) {
        if (write && error.code !== "rejected") {
          this.uncertain = record;
          throw failure("uncertain");
        }
        if (!error.code) throw failure("network");
        throw error;
      } finally {
        clearTimeout(timer);
        if (!settled) request.catch(() => {});
      }
    }
    async read() {
      return validate(
        await this.request((this.routes.cart_url || "/cart") + ".js"),
      );
    }
    refresh() {
      return this.enqueue(async () => this.accept(await this.read()), true);
    }
    change(key, quantity, delay = 250) {
      if (this.locked) return false;
      if (!Number.isSafeInteger(quantity) || quantity < 0) {
        this.error = failure("quantity");
        this.emit();
        return false;
      }
      const signature = this.aliases.get(key);
      if (!signature) {
        this.error = failure("changed");
        this.emit();
        return false;
      }
      const old = this.intents.get(signature);
      clearTimeout(old?.timer);
      const intent = { signature, quantity, timer: null };
      this.intents.set(signature, intent);
      intent.timer = setTimeout(() => this.flush().catch(() => {}), delay);
      this.emit();
      return true;
    }
    flush() {
      for (const intent of this.intents.values()) clearTimeout(intent.timer);
      if (this.flushing) return this.flushing;
      const run = this.enqueue(async () => {
        while (this.intents.size) {
          const [signature, intent] = this.intents.entries().next().value;
          const before = await this.read();
          const item = this.resolve(signature, before);
          if (!item && intent.quantity !== 0) throw failure("changed");
          let next = before;
          if (item && item.quantity !== intent.quantity) {
            const expected = {
              type: "change",
              signature,
              quantity: intent.quantity,
            };
            next = await this.request(
              this.route("change"),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify({
                  id: item.key,
                  quantity: intent.quantity,
                }),
              },
              expected,
            );
            try {
              validate(next);
              // After a checkout return, change.js can include shipping in
              // total_price and return different discount allocations. Never
              // render that write response; reconcile through cart.js, as add
              // already does, while this mutation still owns the queue.
              next = await this.read();
            } catch (_) {
              this.uncertain = { expectation: expected, settled: true };
              throw failure("uncertain");
            }
          }
          this.accept(next);
          if ((this.resolve(signature)?.quantity || 0) !== intent.quantity)
            throw failure("quantity_changed");
          if (this.intents.get(signature) === intent)
            this.intents.delete(signature);
          this.emit();
        }
        return this.cart;
      });
      this.flushing = run.finally(() => {
        this.flushing = null;
      });
      return this.flushing;
    }
    add(body, items, source = "product") {
      if (this.adds.has(source)) return this.adds.get(source);
      if (this.locked) return Promise.reject(failure("locked"));
      if (this.intents.size) this.flush().catch(() => {});
      const run = this.enqueue(async () => {
        const before = await this.read();
        const expected = items.map((item) => {
          const signature = identity(item);
          return {
            signature,
            quantity:
              (this.resolve(signature, before)?.quantity || 0) + item.quantity,
          };
        });
        const expectation = { type: "add", lines: expected };
        const multipart =
          typeof FormData !== "undefined" && body instanceof FormData;
        await this.request(
          this.route("add"),
          {
            method: "POST",
            headers: multipart
              ? { Accept: "application/json" }
              : {
                  Accept: "application/json",
                  "Content-Type": "application/json",
                },
            body: multipart ? body : JSON.stringify(body),
          },
          expectation,
        );
        let next;
        try {
          next = await this.read();
        } catch (_) {
          this.uncertain = { expectation, settled: true };
          throw failure("uncertain");
        }
        this.accept(next);
        if (!this.matches(expectation, next)) throw failure("changed");
        return next;
      });
      this.adds.set(source, run);
      run
        .finally(() => {
          this.adds.delete(source);
        })
        .catch(() => {});
      return run;
    }
    matches(expectation, cart) {
      if (!expectation) return false;
      const lines =
        expectation.type === "add" ? expectation.lines : [expectation];
      return lines.every(
        (line) =>
          (this.resolve(line.signature, cart)?.quantity || 0) === line.quantity,
      );
    }
    async retry() {
      if (this.locked) return;
      await this.tail;
      return this.enqueue(async () => {
        const next = await this.read();
        this.accept(next);
        if (this.uncertain) {
          if (
            !this.uncertain.settled ||
            !this.matches(this.uncertain.expectation, next)
          )
            throw failure("uncertain");
          this.uncertain = null;
        }
        this.error = null;
        this.emit();
      }, true).then(() => (this.intents.size ? this.flush() : this.cart));
    }
    checkout(handoff) {
      if (this.checkoutPromise) return this.checkoutPromise;
      this.locked = true;
      this.emit();
      this.checkoutPromise = (async () => {
        await this.flush();
        await this.tail;
        if (this.error) throw this.error;
        const expected = basket(this.cart);
        const next = await this.read();
        this.accept(next);
        if (basket(next) !== expected) throw failure("changed");
        if (!next.item_count) throw failure("empty");
        await handoff(next);
        return next;
      })().catch((error) => {
        this.error = error;
        this.locked = false;
        this.checkoutPromise = null;
        this.emit();
        throw error;
      });
      return this.checkoutPromise;
    }
    restore() {
      this.locked = false;
      this.checkoutPromise = null;
      this.emit();
      return this.refresh();
    }
  }
  return { Coordinator, identity, basket, validate, properties };
});
